class CustomMenuDesktop extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.activeLevel1 = null;
    this.activeLevel2 = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    // Remove hidden attribute to show element (FOUC prevention)
    this.removeAttribute('hidden');
    
    this.trigger = this.querySelector('.custom-menu-trigger');
    this.menu = this.querySelector('.custom-mega-menu');
    this.level1Links = Array.from(this.querySelectorAll('.custom-level1__link[data-level2]'));
    this.level2Links = Array.from(this.querySelectorAll('.custom-level2__link[data-has-children="true"]'));
    this.level2Panels = Array.from(this.querySelectorAll('.custom-level2__content'));
    this.level3Panels = Array.from(this.querySelectorAll('.custom-level3__content'));

    if (!this.trigger || !this.menu) return;

    this.hideAllPanels();

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleMenu();
    });

    this.level1Links.forEach((link) => {
      link.addEventListener('click', (event) => this.handleLevel1Click(event, link));
    });

    this.level2Links.forEach((link) => {
      link.addEventListener('click', (event) => this.handleLevel2Click(event, link));
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    this.initLevel3SplitColumns();
  }

  initLevel3SplitColumns() {
    this.level3Panels.forEach((panel) => {
      const list = panel.querySelector('.custom-level3__list');
      if (!list) return;
      const itemCount = list.querySelectorAll('.custom-level3__item').length;
      if (itemCount > 5) {
        list.classList.add('has-split-columns');
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  toggleMenu() {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.isOpen = true;
    this.menu.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.classList.remove('has-level2', 'has-level3');
  }

  closeMenu() {
    this.isOpen = false;
    this.menu.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.resetState();
  }

  resetState() {
    this.activeLevel1 = null;
    this.activeLevel2 = null;

    this.level1Links.forEach((link) => {
      link.classList.remove('is-active');
      link.setAttribute('aria-expanded', 'false');
    });

    this.level2Links.forEach((link) => {
      link.classList.remove('is-active');
      link.setAttribute('aria-expanded', 'false');
    });

    this.hideAllPanels();
    this.classList.remove('has-level2', 'has-level3');
  }

  hideAllPanels() {
    this.level2Panels.forEach((panel) => {
      panel.hidden = true;
    });

    this.level3Panels.forEach((panel) => {
      panel.hidden = true;
    });
  }

  handleLevel1Click(event, link) {
    const panelId = link.getAttribute('data-level2');
    if (!panelId) return;

    event.preventDefault();
    if (!this.isOpen) this.openMenu();

    if (this.activeLevel1 === link) {
      link.classList.remove('is-active');
      link.setAttribute('aria-expanded', 'false');
      this.activeLevel1 = null;
      this.activeLevel2 = null;
      this.level2Links.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-expanded', 'false');
      });
      this.level2Panels.forEach((panel) => (panel.hidden = true));
      this.level3Panels.forEach((panel) => (panel.hidden = true));
      this.classList.remove('has-level2', 'has-level3');
      return;
    }

    this.level1Links.forEach((item) => {
      const isCurrent = item === link;
      item.classList.toggle('is-active', isCurrent);
      item.setAttribute('aria-expanded', isCurrent ? 'true' : 'false');
    });

    this.activeLevel1 = link;
    this.activeLevel2 = null;

    this.level2Panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });
    this.classList.add('has-level2');
    this.classList.remove('has-level3');

    this.level2Links.forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-expanded', 'false');
    });

    this.level3Panels.forEach((panel) => {
      panel.hidden = true;
    });
  }

  handleLevel2Click(event, link) {
    const hasChildren = link.getAttribute('data-has-children') === 'true';
    if (!hasChildren) return;

    const panelId = link.getAttribute('data-level3');
    if (!panelId) return;

    event.preventDefault();
    if (!this.isOpen) this.openMenu();

    const level2Panel = link.closest('.custom-level2__content');
    if (!level2Panel || level2Panel.hidden) return;

    if (this.activeLevel2 === link) {
      link.classList.remove('is-active');
      link.setAttribute('aria-expanded', 'false');
      this.activeLevel2 = null;
      this.level3Panels.forEach((panel) => (panel.hidden = true));
      this.classList.remove('has-level3');
      return;
    }

    this.level2Links.forEach((item) => {
      const isCurrent = item === link;
      item.classList.toggle('is-active', isCurrent);
      item.setAttribute('aria-expanded', isCurrent ? 'true' : 'false');
    });

    this.activeLevel2 = link;
    this.level3Panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });
    this.classList.add('has-level3');
  }

  onDocumentClick(event) {
    if (!this.isOpen) return;
    if (!this.contains(event.target)) this.closeMenu();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.closeMenu();
      this.trigger?.focus();
    }
  }
}

if (!customElements.get('custom-menu-desktop')) {
  customElements.define('custom-menu-desktop', CustomMenuDesktop);
}

class CustomMenuMobile extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.panelStack = [];
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.navigateForward = this.navigateForward.bind(this);
    this.navigateBack = this.navigateBack.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // Remove hidden attribute to show element (FOUC prevention)
    this.removeAttribute('hidden');
    
    this.trigger = this.querySelector('.custom-menu-trigger');
    this.drawer = this.querySelector('.custom-mobile-drawer');
    this.closeBtn = this.querySelector('.custom-mobile-drawer__close');
    if (!this.trigger || !this.drawer) return;

    this.trigger.addEventListener('click', this.openDrawer);
    this.closeBtn?.addEventListener('click', this.closeDrawer);

    const overlay = this.drawer.querySelector('.custom-mobile-drawer__overlay');
    overlay?.addEventListener('click', this.closeDrawer);

    this.drawer.querySelectorAll('[data-navigate-forward]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const targetPanel = button.getAttribute('data-target-panel');
        this.navigateForward(targetPanel);
      });
    });

    this.drawer.querySelectorAll('[data-navigate-back]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.navigateBack();
      });
    });

    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  openDrawer(event) {
    event?.preventDefault();
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.panelStack = [];
    this.drawer.querySelectorAll('.custom-mobile-panel').forEach((panel) => {
      panel.classList.remove('is-collapsed-left');
      panel.classList.toggle('is-hidden', panel.id !== 'mobile-panel-root');
    });
  }

  navigateForward(targetPanelId) {
    if (!targetPanelId) return;
    const currentPanel = this.getCurrentPanel();
    const targetPanel = this.drawer.querySelector(`#${targetPanelId}`);
    if (!targetPanel) return;

    if (currentPanel) {
      currentPanel.classList.add('is-collapsed-left');
      this.panelStack.push(currentPanel);
    }

    targetPanel.classList.remove('is-hidden');
  }

  navigateBack() {
    const currentPanel = this.getCurrentPanel();
    const previousPanel = this.panelStack.pop();
    if (!previousPanel) {
      this.closeDrawer();
      return;
    }

    currentPanel?.classList.add('is-hidden');
    previousPanel.classList.remove('is-collapsed-left');
  }

  getCurrentPanel() {
    const panels = this.drawer.querySelectorAll('.custom-mobile-panel');
    for (const panel of panels) {
      if (!panel.classList.contains('is-hidden') && !panel.classList.contains('is-collapsed-left')) {
        return panel;
      }
    }
    return null;
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.closeDrawer();
    }
  }
}

if (!customElements.get('custom-menu-mobile')) {
  customElements.define('custom-menu-mobile', CustomMenuMobile);
}
